import { ArrowUpRight } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { type WebOpportunity } from '../../../lib/opportunities';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { Modal } from '../../Modal';
import { Button, ScrollArea, TextButton, Typography } from '../ui';
import styles from './OpportunitiesModal.module.css';

// The opportunities modal (Figma 12815-166600) — the "View More" on the home pile now opens this
// instead of expanding the list in place. Same data (the agent-proposed OPPORTUNITY goals), same
// strings (home.opportunities.*), and the same "Talk to {name}" handoff into the side chat.
const PAGE = 'home';

export function OpportunitiesModal({
  open,
  onClose,
  opportunities,
  workspaceName,
}: {
  open: boolean;
  onClose: () => void;
  opportunities: WebOpportunity[];
  workspaceName: (opportunity: WebOpportunity) => string | undefined;
}) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();

  const close = () => {
    logModalActionBi({ page_name: PAGE, modal_name: 'opportunities', action: 'close' });
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="small"
      maxHeightPx={650}
      labelledBy="opportunities-title"
      title={t('home.opportunities.heading')}
      description={t('home.opportunities.subheading')}
      headerSeparator
      footerSeparator
      contentClassName={styles.content}
      footer={
        <Button variant="outlined" onClick={close}>
          {t('common.close')}
        </Button>
      }
    >
      <ScrollArea className={styles.list}>
        {opportunities.map((opportunity) => (
          <OpportunityRow key={opportunity.id} opportunity={opportunity} workspace={workspaceName(opportunity)} onDone={onClose} />
        ))}
      </ScrollArea>
    </Modal>
  );
}

function OpportunityRow({
  opportunity,
  workspace,
  onDone,
}: {
  opportunity: WebOpportunity;
  workspace?: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { short } = useAgentName();
  const { logPageClickBi } = useGenericBi();
  const { openChatWithMessage } = useActiveConversation();

  const talk = () => {
    logPageClickBi({ page_name: PAGE, button_name: 'opportunity_talk' }, { componentName: 'opportunity-talk' });
    onDone();
    openChatWithMessage({ kind: 'new', message: t('home.opportunities.chatPrompt', { title: opportunity.title }) });
  };

  return (
    <div className={styles.row}>
      <span className={styles.rowText}>
        <Typography variant="heading-02" color="strong" component="span">
          {opportunity.title}
        </Typography>
        {workspace && (
          <Typography variant="p2" color="secondary" component="span" noWrap>
            {workspace}
          </Typography>
        )}
      </span>
      <TextButton
        size="small"
        className={styles.talk}
        endIcon={<ArrowUpRight size={16} className="dir-flip" aria-hidden />}
        onClick={talk}
      >
        {t('home.opportunities.cta', { name: short })}
      </TextButton>
    </div>
  );
}
